import * as api from "api/Core";
import { GenericLogDialog, GenericLogDialogProps, ILogTranslated } from "components/GenericLogDialog";
import ApprovalStatusName from "components/Translations/ApprovalStatusName";
import FileCognitiveAnalysisReasonName from "components/Translations/FileCognitiveAnalysisReasonName";
import PublicationCognitiveAnalysisReasonName from "components/Translations/PublicationCognitiveAnalysisReasonName";
import { FileCognitiveAnalysisReason, PublicationCognitiveAnalysisReason } from "models/ApiEnums";
import { LogRecord } from "models/ApiModels";
import * as React from 'react';
import { Fragment } from 'react';
import { withTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps extends GenericLogDialogProps {
    publicationId: string;
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class PublicationLogDialog extends GenericLogDialog<IProps> {

    getLog(offset: number, take: number) {
        return api.getPublicationLog(this.props.publicationId, offset, take);
    }

    translate(data: LogRecord[]): ILogTranslated[] {
        const { t } = this.props;
        const result: ILogTranslated[] = [];

        data.forEach((v) => {
            var j = v.jsonData;

            Object.keys(j).forEach((key) => {

                const r: ILogTranslated = {
                    name: this.defaultFieldName(key, "Publication"),
                    content: "?",
                    creationDate: v.creationDate,
                    creator: v.creator,
                };

                switch (key) {
                    case "Created":
                    case "Edited":
                    case "Published":
                    case "AttachsChanged":
                    case "ContentChanged":
                    case "TitleChanged":
                    case "Unblocked":
                    case "CognitiveAnalysisStart":
                    case "MediaEncodeStart":
                        r.content = null;
                        break;

                    case "Blocked":
                        r.content = j[key]["BlockerName"] == null
                            ? null
                            : (
                                <Fragment>
                                    {
                                        j[key]["Message"] &&
                                        <Fragment>
                                            <br />
                                            {j[key]["Message"]}
                                        </Fragment>
                                    }
                                </Fragment>
                            );
                        break;

                    case "Topics":
                        r.content = this.addOrDeleteDictionaryTranslate(j[key] as PascalCaseKeyValueItem<string, string>[]);
                        break;

                    case "Accesses":
                        r.content = this.addOrDeleteDictionaryTranslate(j[key] as PascalCaseKeyValueItem<string, string>[]);
                        break;

                    case "Institutional":
                        r.content = (
                            <Fragment>
                                <div>{t("Glossary.From")}: {j[key]["From"] === true ? t("Views.Publication.Dialogs.PublicationLogDialog.PublicationInstitutional") : t("Views.Publication.Dialogs.PublicationLogDialog.PublicationStandard")}</div>
                                <div>{t("Glossary.To")}: {j[key]["To"] === true ? t("Views.Publication.Dialogs.PublicationLogDialog.PublicationInstitutional") : t("Views.Publication.Dialogs.PublicationLogDialog.PublicationStandard")}</div>
                            </Fragment>
                        );
                        break;

                    case "Approval":
                        r.content = (
                            <Fragment>
                                {
                                    j[key].Result == 1
                                        ? <div style={{ color: "green" }}><b><ApprovalStatusName approvalStatus={j[key].Result} /></b></div>
                                        : <div style={{ color: "red" }}><b><ApprovalStatusName approvalStatus={j[key].Result} /></b></div>
                                }
                                {
                                    j[key].Approver != null &&
                                    <div>{t("Glossary.By")}: <b>{j[key].Approver}</b></div>
                                }
                                <div><i>{j[key].Notes}</i></div>
                            </Fragment>
                        );
                        break;

                    case "CancelCurrentApproval":
                        r.content = t("Publication.CancelCurrentApprovalContent");
                        break;

                    case "ParcialCancelCurrentApproval":
                        r.content = t("Publication.ParcialCancelCurrentApprovalContent");
                        break;

                    case "ApprovalProcessStart":
                        r.content = null;
                        break;

                    case "ApprovalProcessEnd":
                        r.content = (
                            <Fragment>
                                {
                                    j[key].Result == 1 && (j[key].Automatic === true || j[key].SkipReason !== 0)
                                        ? (
                                            <div style={{ color: "grey" }}>
                                                {j[key].Automatic === true && <b>{t("Common.ApprovalStatus.Auto")}</b>}
                                                {j[key].SkipReason && j[key].SkipReason != 0 && <span>({t(`Common.ApprovalStatus.SkipReason_${j[key].SkipReason}`)})</span>}
                                            </div>
                                        )
                                        : j[key].Result == 1
                                            ? (
                                                <div style={{ color: "green" }}>
                                                    <b><ApprovalStatusName approvalStatus={j[key].Result} /></b>
                                                </div>
                                            )
                                            : j[key].Result == 2
                                                ? (
                                                    <div style={{ color: "red" }}>
                                                        <b><ApprovalStatusName approvalStatus={j[key].Result} /></b>
                                                    </div>
                                                )
                                                : ""
                                }
                            </Fragment>
                        );
                        break;

                    case "ReportedAbuseInPublication":
                        r.name = j[key]["Operation"] == "delete" ? t("Common.ReportedAbuseInPublicationDeleted") : t("Common.ReportedAbuseInPublicationAdded");
                        r.content = (<Fragment><span>{t("Glossary.By")}: {j[key]["From"]}</span></Fragment>);
                        break;

                    case "AbuseReportResult":
                        r.content = (
                            <Fragment>
                                <span>{t("Glossary.By")}: {j[key]["Approver"]}</span>
                                <br />
                                <span>{t("Glossary.Result")}: <ApprovalStatusName approvalStatus={j[key]["Result"]} /></span>
                            </Fragment>
                        );
                        break;

                    case "UnblockRequested":
                        r.name = j[key]["Operation"] == "delete" ? t("Common.UnblockRequestPublicationDeleted") : t("Common.UnblockRequestPublicationAdded");
                        r.content = (<Fragment><span>{t("Glossary.By")}: {j[key]["From"]}</span></Fragment>);
                        break;

                    case "UnblockResult":
                        r.content = (
                            <Fragment>
                                <span>{t("Glossary.By")}: {j[key]["Approver"]}</span>
                                <br />
                                <span>{t("Glossary.Result")}: <ApprovalStatusName approvalStatus={j[key]["Result"]} /></span>
                                {
                                    j[key].Notes &&
                                    <>
                                        <br />
                                        <div><i>{j[key].Notes}</i></div>
                                    </>
                                }
                            </Fragment>
                        );
                        break;


                    case "NegativeLikesbalance":
                        r.content = (<Fragment><span>{t("Common.Balance")}: {j[key]["Counter"]}</span></Fragment>);
                        break;

                    case "AnswerAdded":
                        r.content = (
                            <Fragment>
                                <div>{t("Common.Summary")}: {j[key]["Summary"]}</div>
                            </Fragment>
                        );
                        break;

                    case "AnswerDeleted":
                        r.content = (
                            <Fragment>
                                <div>{t("Glossary.From")}: {j[key]["From"]}</div>
                                <div>{t("Common.Summary")}: {j[key]["Summary"]}</div>
                            </Fragment>
                        );
                        break;

                    case "AnswerChanged":
                    case "DraftRemoved":
                    case "ListConvertedToPublic":
                    case "ListConvertedToPrivate":
                    case "AddHighligh":
                    case "CancelHighligh":
                        r.content = null;
                        break;

                    case "CourseRemoveItem":
                    case "CourseNewItem":
                        r.content = this.stringListTranslate(j[key]);
                        break;

                    case "CoursePublished":
                    case "CourseViewPeriodChanged":
                    case "CoursePropertiesChanged":
                        r.content = null;
                        break;

                    case "CourseGainPoints":
                        r.content = this.valueFromToTranslate(j[key]["From"], j[key]["To"]);
                        break;

                    case "MediaEncodeEnd":
                    case "CognitiveAnalysisEnd": {
                        const reasons: React.ReactNode[] = [];
                        const cognitiveAnalysisReasons = j[key]["Reasons"];
                        if (cognitiveAnalysisReasons) {
                            Object.keys(PublicationCognitiveAnalysisReason).forEach((v) => {
                                if ((cognitiveAnalysisReasons & PublicationCognitiveAnalysisReason[v]) > 0)
                                    reasons.push(<PublicationCognitiveAnalysisReasonName value={PublicationCognitiveAnalysisReason[v]} />);
                            });
                        }

                        r.content = (
                            <Fragment>
                                <div>
                                    {
                                        t("Common.AnalysisResult")}: {j[key]["Result"] == true ? t("Common.CognitiveAnalysisResultTrue") : t("Common.CognitiveAnalysisResultFalse")
                                    }
                                </div>
                                {
                                    reasons.length > 0 &&
                                    <div>{t("Common.AnalysisReasons")}: {reasons.map((v, index) => <span key={index}>&bull;&nbsp;{v}&nbsp;</span>)}</div>
                                }
                            </Fragment>
                        );
                        break;
                    }
                }
                result.push(r);
            });
        });

        return result;
    }
}

export default withTranslation("log")(PublicationLogDialog);